import { useState, useEffect, useCallback } from "react";

/**
 * Custom hook for managing state that persists to localStorage
 *
 * @template T - The type of the state value
 * @param key - The localStorage key (should be unique across the app)
 * @param defaultValue - The default value if nothing is in localStorage
 * @returns A tuple of [value, setValue] similar to useState
 *
 * @example
 * const [filters, setFilters] = useLocalStorageState('myFilters', { min: 0, max: 100 });
 */
function useLocalStorageState<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prevValue: T) => T)) => void] {
  // Initialize state from localStorage or use default
  const [value, setValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (item) {
        return JSON.parse(item) as T;
      }
      return defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  // Save to localStorage whenever value changes
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn(`Error saving to localStorage key "${key}":`, error);
    }
  }, [key, value]);

  // Create a wrapped setValue that handles function updates like useState
  const setValueWrapper = useCallback((newValue: T | ((prevValue: T) => T)) => {
    setValue((prevValue) => {
      const valueToStore =
        typeof newValue === "function"
          ? (newValue as (prevValue: T) => T)(prevValue)
          : newValue;
      return valueToStore;
    });
  }, []);

  return [value, setValueWrapper];
}

export default useLocalStorageState;
